import { createReducer, isAnyOf } from '@reduxjs/toolkit';

import { setSelectedGroupOffer } from 'modules/works/actions';
import { fetchEntity, fetchEntitySuccess } from 'modules/entity/actions';
import { acceptRepairOrderSuccess } from 'modules/entity/subEntities/appointment/actions';
import { answerWorksSuccess } from 'modules/entity/subEntities/intervention/actions/works';
import { INTERVENTION_UPDATE_MODAL_TYPES } from 'works/components/Content/components/Common/modalUtils';
import {
  canDisplayAdvice,
  computePriceWithCoef,
  computeDiscount,
  computeDiscountedDepositPrice,
  computeDiscountedPrice,
} from 'works/utils';
import { ADVICE_STATUS } from 'modules/entity/constants';

/**
 * @typedef {{[adviceId: string]: import('types/Advice').Advice}} ByIdState
 */

/** @type {ByIdState} */
const initialState = {};

/**
 * @param {import('types/Advice').Advice} advice
 * @returns {import('types/Advice').Advice}
 */
const formatPrices = (advice, vat) => ({
  ...advice,
  discountPriceWithVAT: computeDiscount(advice, vat),
  discountPriceWithoutVAT: computeDiscount(advice),
  priceWithoutDiscountWithVAT: computePriceWithCoef(advice, vat),
  priceWithoutDiscountWithoutVAT: computePriceWithCoef(advice),
  discountedPriceWithVAT: computeDiscountedPrice(advice, vat),
  discountedPriceWithoutVAT: computeDiscountedPrice(advice),
  discountedDepositPriceWithVAT: computeDiscountedDepositPrice(advice, vat),
  discountedDepositPriceWithoutVAT: computeDiscountedDepositPrice(advice),
});

/**
 * @param {import('modules/entity/subEntities/intervention/types').Intervention} intervention
 * @return {import('types/Advice').Advice[]}
 */
const getInterventionAdvices = ({ checklists = [], valueAddedTax }) =>
  checklists.flatMap(({ isProactive, type, checklistGroupId, items = [] }) =>
    items.flatMap(({ advices = [] }) =>
      advices.map((advice) => formatPrices({ ...advice, checklistType: type, checklistGroupId, isProactive }, valueAddedTax))));

/**
 * @param {ByIdState} state
 * @param {Pick<import('modules/entity/subEntities/intervention/types').FetchInterventionSuccessAction, 'payload'> & Pick<import('modules/entity/subEntities/intervention/types').FetchInterventionSuccessAction, 'meta'>} action
 * @returns {ByIdState}
 */
const setFromEntity = (state, { payload: intervention }) =>
  getInterventionAdvices(intervention).reduce((acc, advice) => {
    // Do not delete, we receive advices to show only in control report
    if (canDisplayAdvice(advice, intervention.displayItemsWithoutPrice)) {
      const oldAdvice = state[advice.adviceId];

      return {
        ...acc,
        [advice.adviceId]: {
          ...advice,
          wasUpdated: advice.status === ADVICE_STATUS.PENDING && (!oldAdvice || oldAdvice.version !== advice.version),
        },
      };
    }

    return acc;
  }, {});

/**
 * @param {ByIdState} state
 * @param {import('modules/entity/subEntities/appointment/types').FetchAppointmentSuccessAction} action
 * @returns {ByIdState}
 */
const setFromAppointment = (state, { payload: appointment }) =>
  setFromEntity(state, { payload: appointment.intervention });

/**
 * @param {ByIdState} state
 * @returns {ByIdState}
 */
const resetWasUpdated = (state, { payload }) => {
  const { type } = payload;

  // `SET_SELECTED_GROUP_OFFER` has no type,
  // And we don't want to reset the wasUpdated flag when the final invoice is updated or new advices are added
  if (!type || type === INTERVENTION_UPDATE_MODAL_TYPES.ADVICE_VERSION) {
    return Object.values(state).reduce(
      (acc, advice) => ({
        ...acc,
        [advice.adviceId]: { ...advice, wasUpdated: false },
      }),
      {}
    );
  }

  return state;
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(acceptRepairOrderSuccess, setFromAppointment)
    .addMatcher(isAnyOf(fetchEntitySuccess.match, answerWorksSuccess.match), setFromEntity)
    .addMatcher(isAnyOf(fetchEntity.match, setSelectedGroupOffer.match), resetWasUpdated);
});
